/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from "react";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { useAppSelector } from "@redux/hooks";
import {
  getSubnetsForReflector,
  getSubnetsForCluster,
  getRouteTablesAndNetworkInterfaces,
  getRouteTablesAndNetworkInterfacesForCluster,
  getCloudElementTagKeysForInstance,
  getCloudElementTagKeysForCluster,
  type CloudElementTagKeyType,
} from "../../request/request";
import { extractSubnets, extractVpcs, extractTagKeyOptions } from "../../../../helpers/formattersShapedData";
import {
  normalizeOutboundFormData,
  type OutboundRuleFormData,
} from "../../../../helpers/cloudPropagationHelpers";
import {
  defaultOutboundRule,
} from "./outboundRuleDefaults";
import OutboundRuleCard from "./components/OutboundRuleCard";

interface OutboundContentProps {
  values: any;
  setValues: (v: any) => void;
  reflectorId?: number;
  clusterId?: number;
  getAccessTokenSilently?: () => Promise<string>;
  propagationRuleId?: number | string;
  // deletedOutboundId is set when the removed rule already existed in the backend,
  // so the parent can defer the actual DELETE call until "Save" is pressed (CTX-154).
  onOutboundRuleDeleted?: (remainingRuleCount: number, deletedOutboundId?: number) => void;
  // True when the associated reflector (standalone) or any instance in the cluster (cluster mode)
  // is below DYNAMIC_TAG_MIN_VERSION — disables "Subnet tags" / "Routing table" (CTX-163).
  disableDynamicTagOptions?: boolean;
}

const validateBgpCommunity = (value: string): string => {
  if (!value) return "";
  const validPattern = /^[0-9:.]*$/;
  if (!validPattern.test(value)) {
    return "BGP community can only contain numbers, colons (:), and dots (.)";
  }
  return "";
};

const validateIPAddress = (value: string): string => {
  if (!value) return "";
  const validPattern = /^[0-9.]*$/;
  if (!validPattern.test(value)) {
    return "IP address can only contain numbers and dots (.)";
  }
  const ipParts = value.split(".");
  if (ipParts.length > 4) return "Invalid IP address format";
  const isCompleteSegment = value.endsWith(".") || ipParts.length === 4;
  for (let i = 0; i < ipParts.length; i++) {
    const part = ipParts[i];
    if (part && (i < ipParts.length - 1 || isCompleteSegment)) {
      const numValue = parseInt(part);
      if (isNaN(numValue) || numValue > 255 || numValue < 0) {
        return "Each IP address segment must be between 0 and 255";
      }
    }
  }
  return "";
};

const OutboundContent: React.FC<OutboundContentProps> = ({
  values,
  setValues,
  reflectorId,
  clusterId,
  getAccessTokenSilently,
  propagationRuleId,
  onOutboundRuleDeleted,
  disableDynamicTagOptions = false,
}) => {
  const { tenantId } = useAppSelector((state: any) => state.selection);
  const [subnetOptions, setSubnetOptions] = useState<string[]>([]);
  const [isLoadingSubnets, setIsLoadingSubnets] = useState(false);
  const [vpcOptions, setVpcOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [isLoadingVpcs, setIsLoadingVpcs] = useState(false);
  const [subnetTagOptions, setSubnetTagOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [routeTableTagOptions, setRouteTableTagOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [isLoadingSubnetTagKeys, setIsLoadingSubnetTagKeys] = useState(false);
  const [isLoadingRouteTableTagKeys, setIsLoadingRouteTableTagKeys] = useState(false);
  const [subnetToAddByRule, setSubnetToAddByRule] = useState<Record<number, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<number, { bgp?: string; ip?: string }>>({});
  const isUpdatingFromParentRef = useRef(false);
  const previousRulesRef = useRef<string>("");

  const [rules, setRules] = useState<OutboundRuleFormData[]>(() => {
    const normalized = normalizeOutboundFormData(values);
    return normalized.rules;
  });

  const [deleteConfirmState, setDeleteConfirmState] = useState<{
    isOpen: boolean;
    ruleIndex: number | null;
    outboundId: number | null;
  }>({ isOpen: false, ruleIndex: null, outboundId: null });

  useEffect(() => {
    if (values?.rules) {
      const next = JSON.stringify(values.rules);
      if (next !== previousRulesRef.current) {
        isUpdatingFromParentRef.current = true;
        previousRulesRef.current = next;
        setRules(normalizeOutboundFormData(values).rules);
      }
    }
  }, [values?.rules]);

  useEffect(() => {
    if (isUpdatingFromParentRef.current) {
      isUpdatingFromParentRef.current = false;
      return;
    }
    const current = JSON.stringify(rules);
    if (current !== previousRulesRef.current) {
      previousRulesRef.current = current;
      setValues({ ...values, rules });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules]);

  useEffect(() => {
    const fetchSubnets = async () => {
      if (!getAccessTokenSilently || (!reflectorId && !clusterId) || !tenantId) return;
      setIsLoadingSubnets(true);
      try {
        const token = await getAccessTokenSilently();
        const response = clusterId
          ? await getSubnetsForCluster(token, clusterId, tenantId)
          : await getSubnetsForReflector(token, reflectorId!, tenantId);
        if (response?.data?.data?.subnets) {
          setSubnetOptions(extractSubnets(response));
        }
      } catch {
        setSubnetOptions([]);
      } finally {
        setIsLoadingSubnets(false);
      }
    };
    fetchSubnets();
  }, [reflectorId, clusterId, getAccessTokenSilently, tenantId]);

  useEffect(() => {
    if (subnetOptions.length === 0) return;
    setRules((prev) =>
      prev.map((rule) => {
        if (rule.subnetConfigMode !== "manual" || !rule.subnets.length) return rule;
        const resolved = rule.subnets.map((s) => {
          if (s.includes(" - ") || /\(\d+\.\d+\.\d+\.\d+\/\d+\)/.test(s)) return s;
          const match = subnetOptions.find((opt) => opt.split(" ")[0] === s);
          return match || s;
        });
        if (resolved.some((r, i) => r !== rule.subnets[i])) {
          return { ...rule, subnets: resolved };
        }
        return rule;
      })
    );
  }, [subnetOptions]);

  useEffect(() => {
    const fetchVpcs = async () => {
      if (!getAccessTokenSilently || (!reflectorId && !clusterId) || !tenantId) return;
      setIsLoadingVpcs(true);
      try {
        const token = await getAccessTokenSilently();
        const response = clusterId
          ? await getRouteTablesAndNetworkInterfacesForCluster(token, clusterId, tenantId)
          : await getRouteTablesAndNetworkInterfaces(token, reflectorId!, tenantId);
        if (response?.data) {
          setVpcOptions(extractVpcs(response));
        }
      } catch {
        setVpcOptions([]);
      } finally {
        setIsLoadingVpcs(false);
      }
    };
    fetchVpcs();
  }, [reflectorId, clusterId, getAccessTokenSilently, tenantId]);

  const fetchTagKeys = useCallback(
    async (elementType: CloudElementTagKeyType) => {
      if (!getAccessTokenSilently || (!reflectorId && !clusterId) || !tenantId) return;

      const setLoading =
        elementType === "subnet" ? setIsLoadingSubnetTagKeys : setIsLoadingRouteTableTagKeys;
      const setOptions =
        elementType === "subnet" ? setSubnetTagOptions : setRouteTableTagOptions;

      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const response = clusterId
          ? await getCloudElementTagKeysForCluster(token, clusterId, tenantId, elementType)
          : await getCloudElementTagKeysForInstance(token, reflectorId!, tenantId, elementType);
        setOptions(extractTagKeyOptions(response));
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [reflectorId, clusterId, getAccessTokenSilently, tenantId]
  );

  const getUsedSubnetIds = (excludeIndex: number): Set<string> => {
    const used = new Set<string>();
    rules.forEach((rule, idx) => {
      if (idx === excludeIndex) return;
      (rule.subnets || []).forEach((s) => used.add(s.split(" ")[0]));
    });
    return used;
  };

  const getFilteredSubnetOptions = (ruleIndex: number) => {
    const used = getUsedSubnetIds(ruleIndex);
    const rule = rules[ruleIndex];
    return subnetOptions.filter((opt) => {
      const optId = opt.split(" ")[0];
      if (used.has(optId)) return false;
      if (rule.subnets.includes(opt)) return false;
      return !rule.subnets.some((s) => s === optId || s.split(" ")[0] === optId);
    });
  };

  const updateRule = (index: number, rule: OutboundRuleFormData) => {
    setRules(rules.map((r, i) => (i === index ? rule : r)));
  };

  const handleDeleteClick = (index: number) => {
    const rule = rules[index];
    if (rule.outboundId) {
      setDeleteConfirmState({
        isOpen: true,
        ruleIndex: index,
        outboundId: rule.outboundId,
      });
    } else {
      setRules(rules.filter((_, i) => i !== index));
    }
  };

  // Removing a rule here only updates local form state — the DELETE call is
  // deferred until the user presses "Save" on the modal, so Cancel/close
  // leaves the persisted rule untouched (CTX-154).
  const handleDeleteConfirm = () => {
    if (deleteConfirmState.ruleIndex == null) {
      setDeleteConfirmState({ isOpen: false, ruleIndex: null, outboundId: null });
      return;
    }
    const remainingRules = rules.filter((_, i) => i !== deleteConfirmState.ruleIndex);
    setRules(remainingRules);
    onOutboundRuleDeleted?.(remainingRules.length, deleteConfirmState.outboundId ?? undefined);
    setDeleteConfirmState({ isOpen: false, ruleIndex: null, outboundId: null });
  };

  return (
    <div className="flex flex-col gap-6 text-body">
      <GenericAlert
        isOpen={deleteConfirmState.isOpen}
        onClose={() =>
          setDeleteConfirmState({ isOpen: false, ruleIndex: null, outboundId: null })
        }
        onConfirm={handleDeleteConfirm}
        title="Delete propagation rule"
        description="Are you sure you want to remove this outbound propagation rule? This won't be applied until you click Save."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="error"
      />

      <div className="rounded-sm border border-semantic-border-secondary p-4 text-semantic-text-secondary text-body">
        Configure how subnets from cloud routing tables are advertised back to this BGP peer.
        Specify subnet prefixes, BGP communities, and next-hop preferences.
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-semantic-text-primary text-body">
          Propagation rules
        </div>
        <GenericButton
          label="+ Add propagation rule"
          size="sm"
          variant="secondary"
          shape="ghost"
          onClick={() => setRules([...rules, defaultOutboundRule()])}
        />
      </div>

      <div className="flex flex-col gap-4">
        {rules.map((rule, idx) => (
          <OutboundRuleCard
            key={idx}
            ruleIndex={idx}
            rule={rule}
            vpcOptions={vpcOptions}
            subnetOptions={getFilteredSubnetOptions(idx)}
            isLoadingVpcs={isLoadingVpcs}
            isLoadingSubnets={isLoadingSubnets}
            subnetTagOptions={subnetTagOptions}
            isLoadingSubnetTagKeys={isLoadingSubnetTagKeys}
            routeTableTagOptions={routeTableTagOptions}
            isLoadingRouteTableTagKeys={isLoadingRouteTableTagKeys}
            onTagKeysNeeded={fetchTagKeys}
            disableDynamicTagOptions={disableDynamicTagOptions}
            subnetToAdd={subnetToAddByRule[idx] || ""}
            onSubnetToAddChange={(v) =>
              setSubnetToAddByRule({ ...subnetToAddByRule, [idx]: v })
            }
            onAddSubnet={(subnet) => {
              if (subnet && !rule.subnets.includes(subnet)) {
                updateRule(idx, { ...rule, subnets: [...rule.subnets, subnet] });
              }
              setSubnetToAddByRule({ ...subnetToAddByRule, [idx]: "" });
            }}
            onRemoveSubnet={(subnet) =>
              updateRule(idx, {
                ...rule,
                subnets: rule.subnets.filter((s) => s !== subnet),
              })
            }
            bgpCommunityError={fieldErrors[idx]?.bgp || ""}
            nexthopIPError={fieldErrors[idx]?.ip || ""}
            onRuleChange={(updated) => updateRule(idx, updated)}
            onDelete={() => handleDeleteClick(idx)}
            canDelete={rules.length > 1}
            onBgpCommunityChange={(v) => {
              const filtered = v.replace(/[^0-9:.]/g, "");
              const err = validateBgpCommunity(filtered);
              setFieldErrors((prev) => ({
                ...prev,
                [idx]: { ...prev[idx], bgp: err },
              }));
              updateRule(idx, { ...rule, bgpCommunity: filtered });
            }}
            onNexthopIPChange={(v) => {
              const filtered = v.replace(/[^0-9.]/g, "");
              const err = validateIPAddress(filtered);
              setFieldErrors((prev) => ({
                ...prev,
                [idx]: { ...prev[idx], ip: err },
              }));
              updateRule(idx, { ...rule, nexthopIPAddress: filtered });
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default OutboundContent;
